import { ANALYSIS_ENGINES } from './vocabulary.js';
import { z } from 'zod';
import { OptomechMaterialUsedSchema } from './optomechMaterials.js';

/**
 * Optomechanical (STOP) runs — spec
 * docs/superpowers/specs/2026-09-24-optomech-fea-calculix-design.md §4.6–4.7.
 *
 * A run takes a model of the conversation's current optical system — one
 * parametric lens, or bodies of its retained CAD file — through fea-service
 * (mesh, CalculiX, the fit) and yields one derived optical system per load
 * case. The derived systems are never conversation versions. The request
 * mirrors fea-service's own model (fea/model.py, spec §0.9): bodies with their
 * materials, named constraints, one load case.
 */

/** fea-service's run stages (fea/runs.py STATUSES), which the backend row mirrors. */
export const OPTOMECH_RUN_STATUSES = ['queued', 'meshing', 'solving', 'exporting', 'sigfit', 'succeeded', 'failed'] as const;
export const OptomechRunStatusSchema = z.enum(OPTOMECH_RUN_STATUSES);
export type OptomechRunStatus = z.infer<typeof OptomechRunStatusSchema>;

/**
 * A glass's index and ABSOLUTE dn/dT with their source (SigFit SOL DNDT, spec
 * §0.4): what the thermo-optic pass of a soak or a gradient integrates through
 * the lens. A property of the glass, so it sits on the material (fea/model.py
 * IsotropicMaterial.thermo_optic), never on the load case: a lens whose
 * material carries it gets its OPD map, one without is named under
 * `results.thermo_optic.without_data` (rule 2).
 */
export const OptomechThermoOpticSchema = z.object({
  /** The index at the run's wavelength. */
  index: z.number().gt(1),
  dndt_per_k: z.number(),
  /** The temperature the index is quoted at; the reference temperature when omitted. */
  index_at_c: z.number().optional(),
  source: z.string().min(1),
});
export type OptomechThermoOptic = z.infer<typeof OptomechThermoOpticSchema>;

/**
 * The properties a run needs, all sourced (spec §4.1 "No material defaults").
 * The mechanical four are required; the rest are what one load case or another
 * may need, optional on the row and refused by name at the case that needs them
 * (fea/model.py IsotropicMaterial, rule 2). The bounds are the ones that class
 * enforces on the solver side. The stress optical coefficient K is not taken
 * inline: it reaches a run only from a library row kept with its datasheet point
 * (§4.8).
 */
/** Standard gravity g_n = 9.80665 m/s², in the solver's mm/s²: what "1 g" means in every gravity vector a run carries. */
export const STANDARD_GRAVITY_MM_S2 = 9806.65;

export const OptomechMaterialSchema = z.object({
  /** 1–80 of [A-Za-z0-9_-]: it becomes the CalculiX *MATERIAL name. */
  name: z.string().regex(/^[A-Za-z0-9_-]{1,80}$/),
  youngs_modulus_mpa: z.number().positive(),
  /** Below the incompressible limit: the bulk modulus E/(3(1 − 2ν)) is singular at ν = 0.5. */
  poisson_ratio: z.number().min(0).lt(0.5),
  density_t_per_mm3: z.number().positive(),
  /**
   * Unbounded in sign on purpose: a low-expansion substrate legitimately sits at or below zero
   * (Shannon §2.3.3 Table 2.4 quotes ULE and Zerodur at ~0.01–0.02e-6/K held to ±0.03e-6/K, a band
   * that crosses zero), so a later tidy-up must not add `.positive()`. z.number() already refuses NaN and ±Infinity.
   */
  cte_per_k: z.number(),
  /** Where the numbers came from (datasheet, catalog file). */
  source: z.string().min(1),
  /** Thermal conductivity in W/(mm·K): what a gradient over more than one body needs on every body (a lone body's steady field does not depend on it). */
  conductivity_w_per_mm_k: z.number().positive().optional(),
  /** Glass only: the index and dn/dT a thermo-optic pass integrates through this body. */
  thermo_optic: OptomechThermoOpticSchema.optional(),
  /**
   * A sourced tensile strength in MPa and where it comes from, both or neither (fea/model.py
   * IsotropicMaterial): what the stress report's margin is screened against. The catalog's or the
   * datasheet's figure, never estimated; a body without one gets no margin.
   */
  tensile_strength_mpa: z.number().positive().optional(),
  strength_source: z.string().min(1).optional(),
}).refine((m) => (m.tensile_strength_mpa === undefined) === (m.strength_source === undefined), { message: 'tensile_strength_mpa and strength_source come together: the figure and where it comes from' });
export type OptomechMaterial = z.infer<typeof OptomechMaterialSchema>;

/**
 * A material named by reference instead of by its numbers: the STABLE id of an
 * optomech_materials row the conversation's project can read (global, or its
 * own). The backend expands it to the inline numbers fea-service takes, at the
 * run's reference temperature and over the load case's interval, and records
 * the version it used (spec 2026-10-16 §4.1.2, §6.2).
 */
export const OptomechMaterialRefSchema = z.object({ material_ref: z.string().uuid() });
/** The body's material is its binding for the retained file (GET /optomech/bodies). Explicit on purpose: nothing is implied by omission. */
export const OptomechMaterialFromBindingSchema = z.object({ from_binding: z.literal(true) });

/** A body's material: inline sourced numbers, a library reference, or the body's binding (retained geometry only; a parametric lens has no body to bind). */
export const OptomechBodyMaterialSchema = z.union([OptomechMaterialSchema, OptomechMaterialRefSchema, OptomechMaterialFromBindingSchema]);
export type OptomechBodyMaterial = z.infer<typeof OptomechBodyMaterialSchema>;

/**
 * One body of the run, with its own sourced material. A lens is named by its
 * FRONT surface number in the current system (its rear is the next surface; a
 * lens of the retained file owns the surfaces the import recorded for it); a
 * body of the retained file — a mount, or a lens — by the occurrence path the
 * CAD import or the composition build recorded (GET /optomech/bodies lists
 * them). Without `geometry` the run is the parametric singlet (fea/geometry.py)
 * and takes exactly one lens by surface.
 */
export const OptomechRunBodySchema = z.union([
  z.object({ surface: z.number().int().positive(), material: OptomechBodyMaterialSchema }),
  z.object({ occurrence_path: z.string().min(1), material: OptomechBodyMaterialSchema }),
]);
export type OptomechRunBody = z.infer<typeof OptomechRunBodySchema>;

/**
 * Mesh the bodies out of the CAD import's or the composition build's retained
 * STEP/IGES (`constraints.mechanical.retained_geometry_ref`) instead of the
 * parametric singlet. The datum and which body is which element are read from
 * the SAVED system (`constraints.mechanical`, `provenance.cad_import` or
 * `provenance.optomech_composition`), never from this body: what is here is
 * checked against them.
 */
export const OptomechGeometrySchema = z.object({
  source: z.literal('retained_cad'),
  /** Must equal the saved system's `constraints.mechanical.retained_geometry_ref`. */
  retained_geometry_ref: z.string().min(1),
});
export type OptomechGeometry = z.infer<typeof OptomechGeometrySchema>;

/** A declared contact circle on one face of a lens: `radius_mm` about the axis, `band_mm` either side (gate G7, spec §0.6; no solved contact patch). */
const band = {
  /** The lens face, by its surface number in the current system. */
  surface: z.number().int().positive(),
  radius_mm: z.number().positive(),
  band_mm: z.number().positive(),
};

/**
 * How the bodies are held and pressed: the idealisations of fea/model.py, in
 * the prescription's vocabulary (a face by its surface number, a body by its
 * occurrence path). Which constraints a load case engages is the case's table
 * (fea/cases.py `check`): a soak and a gradient run free whatever is declared;
 * gravity engages the holds, ties, mount contact and pad seat and refuses a
 * body nothing reaches; a preload engages its ring, the seat and the holds.
 */
/**
 * A body held in its fixture (also a composition's holds, optomechAssembly.ts): `outer_cylinder` or `all` fix
 * those faces in x, y and z (the housing clamped, so it cannot expand there); `end_face` holds its end face away
 * from the pressed ring along the axis alone, so it expands freely from that face: the contact seat model's hold
 * (seat_model 'contact' holds a composition's tube that way, and refuses the other two).
 */
export const OPTOMECH_HOLD_FACES = ['outer_cylinder', 'all', 'end_face'] as const;
export const OptomechAssemblyHoldSchema = z.object({ body: z.string().min(1), faces: z.enum(OPTOMECH_HOLD_FACES).optional() });
export type OptomechAssemblyHold = z.infer<typeof OptomechAssemblyHoldSchema>;

// Each variant is exported on its own so the agent's optomechRun schema can describe these fields without copying them.
/** Retained geometry only. */
export const OptomechHoldConstraintSchema = OptomechAssemblyHoldSchema.extend({ kind: z.literal('hold') });
/**
 * Nodes of `slave` within `tolerance_mm` of `master`'s faces follow them (a CalculiX
 * tie). Gravity only: a tie is a rigid link that does not expand, so a soak joins
 * bodies where the CAD touches and holds every other group of bodies on its own
 * statically determinate support (spec §0.5). Retained geometry only.
 */
export const OptomechTieConstraintSchema = z.object({ kind: z.literal('tie'), slave: z.string().min(1), master: z.string().min(1), tolerance_mm: z.number().positive() });
/**
 * One lens of the retained file held where it touches the file's UNMESHED mount
 * solids (fea/cad.py's contact search): `body` is that lens (the run's only
 * body, so it may be omitted), `mounts` which `non_element_solids` of role
 * `mount` touch it (omitted: all of them). A mount that does not touch is
 * reported by its clearance, and a gravity run with no contact is refused.
 */
export const OptomechMountContactConstraintSchema = z.object({ kind: z.literal('mount_contact'), body: z.string().min(1).optional(), mounts: z.array(z.string().min(1)).optional() });
/** Three hard pads at 0°/120°/240° on the circle of `radius_mm` of a lens face (gate G4): the parametric singlet's seat under gravity. */
export const OptomechPadSeatConstraintSchema = z.object({ kind: z.literal('pad_seat'), surface: z.number().int().positive(), radius_mm: z.number().positive(), pad_radius_mm: z.number().positive() });
/** A band of a lens face held along the axis: the seat a preload presses the lens onto (the ring band is on the opposite face). */
export const OptomechSeatBandConstraintSchema = z.object({ kind: z.literal('seat_band'), ...band });
/** A band of a lens face a retaining ring's force is spread over as one pressure (the preload's ring, parametric singlet). */
export const OptomechRingBandConstraintSchema = z.object({ kind: z.literal('ring_band'), ...band });
/**
 * A retaining ring that is a BODY of the retained file pressing a lens face
 * (spec 2026-10-19 §6.1): its force is one pressure over its outer annular
 * face, the lens rests on the circle its other carrier imprints. `ring` by
 * occurrence path; `radius_mm` where it bears on the face, `band_mm` the
 * declared contact half-width. Neither has a default.
 */
export const OptomechRingBodyConstraintSchema = z.object({ kind: z.literal('ring_body'), ring: z.string().min(1), ...band });

export const OptomechConstraintSchema = z.discriminatedUnion('kind', [
  OptomechHoldConstraintSchema,
  OptomechTieConstraintSchema,
  OptomechMountContactConstraintSchema,
  OptomechPadSeatConstraintSchema,
  OptomechSeatBandConstraintSchema,
  OptomechRingBandConstraintSchema,
  OptomechRingBodyConstraintSchema,
]);
export type OptomechConstraint = z.infer<typeof OptomechConstraintSchema>;

/** A gradient's boundary key: a face by surface number (`S3`), the one lens's edge (`edge`), or a body's edge by path (`asm/L1.edge`). */
export const OPTOMECH_BOUNDARY_KEY_RE = /^(S[1-9]\d*|edge|.+\.edge)$/;

/** A uniform temperature change from the reference temperature; the bodies free (each group joined where the CAD touches, on its own support). */
export const OptomechSoakCaseSchema = z.object({ kind: z.literal('soak'), delta_t_c: z.number() });
/**
 * The temperature field a steady heat solve gives for temperatures (°C) fixed
 * on face sets — a face by its surface number (`S<n>`), the rest of a body's
 * outside by `edge` (one lens) or `<occurrence path>.edge` — at least two of
 * them, then the deformation under that field, free. One homogeneous body's
 * field does not depend on its conductivity; over several bodies every body's
 * material must carry `conductivity_w_per_mm_k`.
 */
export const OptomechGradientCaseSchema = z.object({
  kind: z.literal('gradient'),
  boundary: z.record(z.string().regex(OPTOMECH_BOUNDARY_KEY_RE), z.number()).refine((b) => Object.keys(b).length >= 2, { message: 'a gradient needs temperatures on at least two face sets' }),
});

// A plain union, not a discriminated one: the two preload forms share the discriminator (Zod refuses a duplicate value).
export const OptomechLoadCaseSchema = z.union([
  OptomechSoakCaseSchema,
  OptomechGradientCaseSchema,
  /**
   * Gravity in the prescription frame: z along the optical axis in the direction
   * light travels, from the vertex of surface 1 (for retained geometry, the frame
   * after the datum transform; its x/y roll is the minimal rotation taking the
   * STEP axis onto +z, not a bench axis). A parametric singlet on its pad seat
   * and a lens on its mount contact take ±z only (fea/cases.py); bodies held
   * by holds take any direction.
   */
  z.object({ kind: z.literal('gravity'), g_vector_mm_s2: z.tuple([z.number(), z.number(), z.number()]) }),
  /**
   * A retaining ring's force on a seated lens: `force_n` on the run's one ring
   * constraint (`ring_band` on the singlet, with a `seat_band` on the opposite
   * face; `ring_body` on retained geometry, with the holds), optionally under
   * 1 g along the axis (a lateral component is refused: the seat carries axial
   * load alone). fea-service reports the seat's reaction against the load it
   * should carry (gate G7).
   */
  z.object({
    kind: z.literal('preload'),
    force_n: z.number().positive(),
    g_vector_mm_s2: z.tuple([z.number(), z.number(), z.number()]).optional(),
  /**
   * Optional: a uniform temperature change (°C) from the reference temperature
   * applied WITH the preload, on the preload's own assembly (fea/cases.py
   * Preload.delta_t_c). fea-service solves the pressure alone, the temperature
   * alone and both on one mesh — the combined solve is the run's field — and
   * reports `preload.thermal`: the seat's load in each solve, the change, the
   * pressed lens's peak stress per solve and the solve times. The pressed lens
   * and ring touch the rest of the model only at the seat, so their statics fix
   * the seat's total load whatever the temperature (the basis says so); refused
   * past the three-solve node budget.
   */
    delta_t_c: z.number().finite().optional(),
  }).strict(),
  /**
   * The same case read off a BUILT composition (POST /optomech/models, then
   * /build): the ring body, the lens face, the force, the radius and the band
   * from its `preload` joint, the paths from its build's provenance, and the
   * holds, when the request declares none, from the model. The composition's
   * built file must be the one `geometry` names, and the current system the
   * version its build wrote.
   */
  z.object({
    kind: z.literal('preload'),
    from_model: z.string().uuid(),
    g_vector_mm_s2: z.tuple([z.number(), z.number(), z.number()]).optional(),
    /** As on the `force_n` form: a uniform temperature change applied with the composition's preload. */
    delta_t_c: z.number().finite().optional(),
  }).strict(),
]);
export type OptomechLoadCase = z.infer<typeof OptomechLoadCaseSchema>;

/**
 * How the seats of a model that has them are solved (fea-service runs.py SEAT_MODEL_MEANING): `bonded`, every
 * seat a band of nodes the lens shares with its carrier (tension and shear, never opens; the stress at assembly,
 * and under a temperature change a bonded-joint artefact at the seats); `contact`, every seat a contact pair with
 * friction that pushes, slides and opens, the ring tightened to the preload and locked, a temperature change
 * reporting the remaining preload and the lift-off temperature (FEA spec §4.3 "Contact seats").
 */
export const OPTOMECH_SEAT_MODELS = ['bonded', 'contact'] as const;
export type OptomechSeatModel = typeof OPTOMECH_SEAT_MODELS[number];

/**
 * Body of POST /optomech/runs: the model fea-service solves (fea/model.py), in
 * the prescription's vocabulary — the bodies with their materials, how they are
 * held, and one load case. The prescription of every lens (radii, thickness,
 * semi-diameter) comes from the conversation's current system.
 */
export const OptomechRunRequestSchema = z.object({
  conversation_id: z.string().uuid(),
  reference_temperature_c: z.number(),
  /** Omitted: the system's primary wavelength. The fit reports Zernike terms in waves at this wavelength. */
  wavelength_um: z.number().positive().optional(),
  /** Absent: the parametric singlet built from the prescription, one lens by surface. */
  geometry: OptomechGeometrySchema.optional(),
  bodies: z.array(OptomechRunBodySchema).min(1),
  constraints: z.array(OptomechConstraintSchema).default([]),
  load_case: OptomechLoadCaseSchema,
  /**
   * Required on a run whose model has seats (a composition's preload, or a declared ring_body), refused on any
   * other; no default. `contact` runs a composition's preload only (its seats are declared joints), needs a
   * sourced friction coefficient for every seat's material pair (src/data/optomech-materials/friction-pairs.json),
   * and is capped at 300 s of solving, a node cap per deck and seat count from measured solves (fea-service refuses a mesh past
   * it, and a deck never measured to finish, by name).
   */
  seat_model: z.enum(OPTOMECH_SEAT_MODELS).optional(),
  mesh: z.object({
    element_size_mm: z.number().positive(),
    /**
     * Solve the case again at element_size_mm / √2 and report each body's peak von Mises and max principal
     * change as `results.stress.convergence` (fea/stress.py). The fit, the viewer fields and every other
     * result stay the first mesh's; the fine mesh must also fit under fea-service's node cap.
     */
    convergence: z.boolean().optional(),
  }),
});
export type OptomechRunRequest = z.infer<typeof OptomechRunRequestSchema>;
/** The request as a caller writes it (`constraints` may be left out). */
export type OptomechRunRequestInput = z.input<typeof OptomechRunRequestSchema>;

// --- fea-service's request (fea-service/fea/runs.py RunRequest) ----------------------------------------------------
// What the backend sends fea-service and stores on the run row as `request`: the model in the solver's vocabulary
// (bodies by name, faces by surface number, SI-mm units). The bounds are fea/runs.py's own Field() bounds.

/** fea/runs.py MaterialSpec (fea/model.py IsotropicMaterial): every property a load case may need, on the body. */
export const FeaMaterialSchema = z.object({
  name: z.string(),
  youngs_modulus_mpa: z.number(),
  poisson_ratio: z.number(),
  density_t_per_mm3: z.number(),
  cte_per_k: z.number(),
  source: z.string(),
  /**
   * The glass's stress optical coefficient K (FEA spec §4.8): with it a lens body
   * gets its stress birefringence OPD map; without it, no map and the body is named
   * in `results.birefringence.without_k`. A mount's value is ignored.
   */
  stress_optical_k_mm2_per_n: z.number().optional(),
  /** Thermal conductivity, which a gradient over more than one body needs on every body. */
  conductivity_w_per_mm_k: z.number().optional(),
  /** With it a lens body gets its thermo-optic OPD under a soak or a gradient; without it the lens is named in `results.thermo_optic.without_data`. */
  thermo_optic: OptomechThermoOpticSchema.optional(),
  /** A sourced tensile strength, both or neither: the stress report's margin for the body is taken against it; without it the body has no margin. */
  tensile_strength_mpa: z.number().optional(),
  strength_source: z.string().optional(),
});
export type FeaMaterial = z.infer<typeof FeaMaterialSchema>;

/** fea/runs.py SurfaceSpec: a prescription row a lens body carries, in the prescription frame. */
export const FeaSurfaceSchema = z.object({
  number: z.number().int().positive(),
  radius_mm: z.number().nullable(),
  vertex_z_mm: z.number(),
  semi_diameter_mm: z.number().positive(),
  conic: z.number().optional(),
});
export type FeaSurface = z.infer<typeof FeaSurfaceSchema>;

/** fea/runs.py ShapeSpec: a parametric spherical singlet (fea/geometry.py Singlet). */
export const FeaShapeSchema = z.object({ r1: z.number(), r2: z.number(), thickness: z.number(), semi_diameter: z.number() });
export type FeaShape = z.infer<typeof FeaShapeSchema>;

/** fea/runs.py BodySpec: a solid of the model, named by its occurrence path (a STEP body) or its instance (a parametric one). */
export const FeaBodySchema = z.object({
  name: z.string().min(1),
  kind: z.enum(['lens', 'mount']),
  material: FeaMaterialSchema,
  surfaces: z.array(FeaSurfaceSchema).optional(),
  shape: FeaShapeSchema.optional(),
});
export type FeaBody = z.infer<typeof FeaBodySchema>;

const vec3 = z.tuple([z.number(), z.number(), z.number()]);

/** fea/runs.py CadDatumSpec: `constraints.mechanical.datum` of the saved system, STEP world → prescription frame. */
export const FeaDatumSchema = z.object({ origin: vec3, axis: vec3, frame: z.literal('step_world_mm') });
export type FeaDatum = z.infer<typeof FeaDatumSchema>;

/** fea/runs.py ParametricGeometrySpec | StepGeometrySpec: a step geometry's file travels as the multipart `file` part of POST /runs/step. */
export const FeaGeometrySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('parametric') }),
  z.object({ kind: z.literal('step'), retained_geometry_ref: z.string(), file_name: z.string(), datum: FeaDatumSchema }),
]);
export type FeaGeometry = z.infer<typeof FeaGeometrySchema>;

/** fea/runs.py FrictionSpec: a seat's material-pair friction with its source, on a seat_model 'contact' run only. */
const FeaFrictionSchema = z.object({ mu: z.number().positive(), source: z.string().min(1) });
const feaBand = { name: z.string().min(1), body: z.string(), surface: z.number().int(), radius_mm: z.number(), band_mm: z.number() };

/** fea/runs.py's constraint specs (fea/model.py): the named idealisations of how the bodies are held and pressed. */
export const FeaConstraintSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('hold'), name: z.string().min(1), body: z.string(), faces: z.enum(OPTOMECH_HOLD_FACES).optional() }),
  z.object({ kind: z.literal('tie'), name: z.string().min(1), slave: z.string(), master: z.string(), tolerance_mm: z.number().positive() }),
  z.object({ kind: z.literal('mount_contact'), name: z.string().min(1), body: z.string(), mounts: z.array(z.string()) }),
  z.object({ kind: z.literal('pad_seat'), name: z.string().min(1), body: z.string(), surface: z.number().int(), radius_mm: z.number(), pad_radius_mm: z.number() }),
  z.object({ kind: z.literal('seat_band'), ...feaBand }),
  z.object({ kind: z.literal('ring_band'), ...feaBand }),
  z.object({
    kind: z.literal('ring_body'), name: z.string().min(1), ring: z.string(), lens: z.string(), surface: z.number().int(), radius_mm: z.number(), band_mm: z.number(),
    /** The ring-on-glass pair's friction, on a seat_model 'contact' run only. */
    contact: FeaFrictionSchema.optional(),
  }),
  /** A seat of the composition (fea/model.py SeatJoint): the lens body on its carrier's lipped face, within band_mm of the seat circle. */
  z.object({
    kind: z.literal('seat_joint'), name: z.string().min(1), lens: z.string(), carrier: z.string(), surface: z.number().int(), radius_mm: z.number(), band_mm: z.number(),
    contact: FeaFrictionSchema.optional(),
  }),
  /**
   * Two consecutive bodies of a cemented element joined on their shared face (fea/model.py CementJoint): a perfect
   * bond under either seat model, verified after meshing. `a` is the front body, which carries `surface`.
   */
  z.object({ kind: z.literal('cement_joint'), name: z.string().min(1), a: z.string(), b: z.string(), surface: z.number().int() }),
]);
export type FeaConstraint = z.infer<typeof FeaConstraintSchema>;

/** fea/runs.py ModelSpec: what is physically there, whatever the load case. */
export const FeaModelSchema = z.object({
  geometry: FeaGeometrySchema,
  bodies: z.array(FeaBodySchema).min(1),
  constraints: z.array(FeaConstraintSchema),
  reference_temperature_c: z.number(),
});
export type FeaModel = z.infer<typeof FeaModelSchema>;

/** fea/runs.py's load cases (fea/cases.py): an operation on the model. A gradient's boundary keys are `<body>.S<n>` or `<body>.edge`. */
export const FeaLoadCaseSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('soak'), delta_t_c: z.number() }),
  z.object({ kind: z.literal('gradient'), boundary: z.record(z.string(), z.number()) }),
  z.object({ kind: z.literal('gravity'), g_vector_mm_s2: vec3 }),
  z.object({ kind: z.literal('preload'), ring: z.string(), force_n: z.number(), g_vector_mm_s2: vec3.optional(), delta_t_c: z.number().optional() }),
]);
export type FeaLoadCase = z.infer<typeof FeaLoadCaseSchema>;

/** fea/runs.py RunRequest. */
export const FeaRunRequestSchema = z.object({
  model: FeaModelSchema,
  load_case: FeaLoadCaseSchema,
  wavelength_mm: z.number().positive(),
  mesh: z.object({ element_size_mm: z.number().positive(), convergence: z.boolean().optional() }),
  /** How a model's seats are solved (fea/runs.py SEAT_MODEL_MEANING): required on a model with seats or a ring body, refused on any other. */
  seat_model: z.enum(OPTOMECH_SEAT_MODELS).optional(),
});
export type FeaRunRequest = z.infer<typeof FeaRunRequestSchema>;

/**
 * A run row's `request`: what went to fea-service. A row from before the one model (2026-10-26, #7540) has no
 * `model` and another shape, served as it is stored; every later row is the typed request.
 */
export const OptomechStoredRequestSchema = z.union([
  FeaRunRequestSchema,
  z.record(z.string(), z.unknown()).refine((r) => !('model' in r), { message: 'a request with a model is the typed fea-service request' }),
  // Both variants are objects; saying so keeps the served schema `type: object` (what it was as a plain record), which the contract gate reads as a narrowing rather than a type change.
]).meta({ type: 'object', propertyNames: { type: 'string' } });
export type OptomechStoredRequest = z.infer<typeof OptomechStoredRequestSchema>;
export const isFeaRunRequest = (r: OptomechStoredRequest): r is FeaRunRequest => 'model' in r;

/** applySigFit's per-surface summary for one derived case. */
export const OptomechAppliedSurfaceSchema = z.object({
  nominal_surface: z.number().int(),
  derived_surface: z.number().int(),
  rigid_body_break: z.boolean(),
  piston_mm: z.number(),
  d_radius_mm: z.number(),
  zernike_terms: z.number().int(),
  residual_rms_mm: z.number(),
  /** The derived row of the Zernike Phase screen carrying the surface's thermo-optic OPD, when the run had one. */
  opd_screen_surface: z.number().int().optional(),
  opd_terms: z.number().int().optional(),
  opd_rms_waves: z.number().optional(),
});

export const OptomechCaseSummarySchema = z.object({
  load_case: z.number().int(),
  /** 1.0 for a static case; the frequency in Hz for a mode. */
  step_value: z.number(),
  surfaces: z.array(OptomechAppliedSurfaceSchema),
  /** Every thickness solve other than fixed/variable, frozen to `fixed` at the nominal value in the derived system: it is the lens as built, compared at the nominal's image plane (a live `marginal_height` image gap would refocus). The nominal keeps them. */
  frozen_thickness_solves: z.array(z.object({
    nominal_surface: z.number().int(),
    solve: z.string(),
    value_mm: z.number(),
  })),
  /** Every radius solve other than fixed/variable, frozen to `fixed` at the nominal radius in the derived system, SigFit's radius change then added: a pickup of a deformed face would copy its change onto a surface that did not deform, and a traced solve (`fnumber`, …) would re-bend its surface. `value_mm` is null for a flat. The nominal keeps them. */
  frozen_radius_solves: z.array(z.object({
    nominal_surface: z.number().int(),
    solve: z.string(),
    value_mm: z.number().nullable(),
  })),
});

/**
 * Which derivative of the retained CAD geometry the run meshed (spec 2026-10-19 §4.5, §7):
 * the raw upload (`constraints.mechanical.retained_geometry_ref`) or its defeatured
 * sibling (`defeatured_geometry_ref`), with the recipe that produced it. `reason` is set
 * when the sibling was named but could not be read, so the raw file was meshed instead.
 */
export const OptomechGeometryUsedSchema = z.object({
  kind: z.enum(['raw', 'defeatured']),
  /** The `project_file:` ref of the file that was meshed. */
  ref: z.string(),
  /** The derivative's recipe ('defeature') and that recipe's semver; both null for the raw file. */
  recipe: z.string().nullable(),
  recipe_version: z.string().nullable(),
  reason: z.string().optional(),
});
export type OptomechGeometryUsed = z.infer<typeof OptomechGeometryUsedSchema>;

/**
 * One lens body's stress birefringence OPD map (FEA spec 2026-09-24 §4.8, rule 11):
 * the polarisation OPD difference Δs = |K| · √(A² + B²) along axis-parallel chords
 * through the body on a 64 × 64 pupil grid over its clear aperture, with
 * A = ∫(σxx − σyy) dz and B = ∫2σxy dz the two component integrals of the in-plane
 * stress deviator and K the glass's stress optical coefficient (TIE-27 §2.1–2.2).
 * That is the first-order composition of the chord's thin retarders: crossed stress
 * directions cancel, and the magnitude sum ∫Δσ_t dz is an upper bound that is not
 * reported. Nothing else: the fast axis and the mean index change are not reported
 * (rule 11).
 */
export const OptomechBirefringenceBodySchema = z.object({
  /** The SIGNED stress optical coefficient as the material row carried it (TIE-27 states K can be negative for some glass types); the map uses its magnitude. */
  k_mm2_per_n: z.number(),
  k_source: z.string(),
  /** The run's wavelength: what `first_order_ok` reads the retardance 2π·Δs/λ at; the map itself is in nm. */
  wavelength_um: z.number(),
  /** The clear aperture the grid spans: the deck's SURAPER semi-diameter. */
  aperture_mm: z.number(),
  grid: z.object({
    x_mm: z.array(z.number()),
    y_mm: z.array(z.number()),
    /** |K|·√(A² + B²) in nm; null outside the aperture or where no chord crosses the body. */
    opd_nm: z.array(z.array(z.number().nullable())),
    /** The chord's path length through the body in mm; null where opd_nm is. */
    path_mm: z.array(z.array(z.number().nullable())),
  }),
  max_nm: z.number(),
  rms_nm: z.number(),
  /** Δs / (t / 10): the per-centimetre value TIE-27 Table 1 is read against. */
  max_nm_per_cm: z.number(),
  mean_nm_per_cm: z.number(),
  /**
   * True when every cell's retardance 2π·Δs/λ is ≤ 0.2 rad at the run's wavelength (19 nm at
   * 587 nm), where the first-order composition holds to within 5 % (§4.8); false when a cell
   * exceeds that, and the map is then approximate.
   */
  first_order_ok: z.boolean(),
});
export type OptomechBirefringenceBody = z.infer<typeof OptomechBirefringenceBodySchema>;

/**
 * `results.birefringence` of a run (FEA spec §4.8's response contract): absent on
 * runs from before it existed and on runs where no lens material carried K.
 * Keyed by the lens occurrence path, or `lens` for a singlet.
 */
export const OptomechBirefringenceSchema = z.object({
  /** The chord choice, the Δσ_t definition and the units, in fea-service's words. */
  basis: z.string(),
  bodies: z.record(z.string(), OptomechBirefringenceBodySchema),
  /** Lens bodies whose material carried no K: no map for them, named rather than defaulted (rule 2). */
  without_k: z.array(z.string()),
});
export type OptomechBirefringence = z.infer<typeof OptomechBirefringenceSchema>;

/**
 * SCHOTT TIE-27 "Stress in optical glass" (July 2004) Table 1: the permissible
 * optical path difference per cm of glass path, by application. The limits are
 * the table's; which value of a body is judged against them is the reader's
 * choice and is stated where the class is shown.
 */
export const TIE27_TABLE_1 = [
  { id: 'polarisation_interference', limit_nm_per_cm: 2, application: 'polarisation and interference instruments' },
  { id: 'precision_astronomical', limit_nm_per_cm: 5, application: 'precision and astronomical optics' },
  { id: 'photographic_microscope', limit_nm_per_cm: 10, application: 'photographic and microscope optics' },
  { id: 'magnifying', limit_nm_per_cm: 20, application: 'magnifying glasses' },
  { id: 'illumination', limit_nm_per_cm: null, application: 'illumination optics (no requirement)' },
] as const;
export type Tie27Table1Row = (typeof TIE27_TABLE_1)[number];

/** The first Table 1 row whose limit a per-cm value stays under (the table writes the first as "< 2 nm/cm"); the last row, with no requirement, otherwise. */
export function tie27Table1Class(nmPerCm: number): Tie27Table1Row {
  return TIE27_TABLE_1.find((row) => row.limit_nm_per_cm !== null && nmPerCm < row.limit_nm_per_cm) ?? TIE27_TABLE_1[TIE27_TABLE_1.length - 1];
}

/**
 * The class a per-cm value falls in, with the value as it is to be SHOWN beside
 * that class: the shortest decimal of at least three significant digits that
 * still falls in the same row. The raw value decides; the print follows it, so
 * 1.9996 nm/cm judged under 2 nm/cm never reads "2 nm/cm is under 2 nm/cm".
 */
export function tie27Table1Reading(nmPerCm: number): Tie27Table1Row & { shown: string } {
  const row = tie27Table1Class(nmPerCm);
  for (let digits = 3; digits <= 10; digits++) {
    const shown = Number(nmPerCm.toPrecision(digits)).toString();
    if (tie27Table1Class(Number(shown)).id === row.id) return { ...row, shown };
  }
  return { ...row, shown: nmPerCm.toString() };
}

// --- fea-service's results (fea/sigfit_results.py, fea/stress.py, fea/cases.py, fea/contact.py) -------------------
// Plain types, not schemas: `OptomechRun.results` is served as stored (a record), and these say what it holds.

export interface SigFitSurfaceResult {
  /** SigFit's own surface id. */
  surface: number;
  /** The surface's number in the optical model (the deck's OPTID). */
  optic_surface: number;
  /** The prescription the deck declared: what the results were fitted for. */
  base_radius_mm: number | null;
  base_conic: number;
  semi_aperture_mm: number;
  rigid_body_mm_rad: [number, number, number, number, number, number];
  d_radius_mm: number;
  subtracted_rms_mm: number;
  subtracted_pv_mm: number;
  norm_radius_mm: number;
  zernike_mm: number[];
  residual_rms_mm: number;
  residual_pv_mm: number;
}

export interface SigFitLoadCaseResult {
  load_case: number;
  subcase: number;
  step: number;
  step_value: number;
  surfaces: SigFitSurfaceResult[];
}

/**
 * A thermo-optic (SOL DNDT) pass's OPD map on an entrance surface, in WAVES at
 * `SigFitDndtResults.wavelength_mm` (fea/sigfit_results.py OpdResult). The
 * Zernike terms are Zemax Standard, over `norm_radius_mm`, and are the very
 * numbers SigFit's own Zemax macro writes into a Zernike Standard Phase surface.
 */
export interface SigFitOpdResult {
  surface: number;
  optic_surface: number;
  semi_aperture_mm: number;
  norm_radius_mm: number;
  opd_zernike_waves: number[];
  opd_rms_waves: number;
  opd_pv_waves: number;
  opd_max_waves: number;
  opd_min_waves: number;
  opd_avg_waves: number;
  residual_rms_waves: number;
  residual_pv_waves: number;
}

export interface SigFitDndtResults {
  deck: string;
  wavelength_mm: number;
  load_cases: { load_case: number; surfaces: SigFitOpdResult[] }[];
}

/** Where a stress point sits: its node, position, the optical surface it lies on and its distance to a constraint. */
export interface FeaStressPoint {
  node: number;
  xyz_mm: [number, number, number];
  /** The optical surface the node lies on, or null off the faces. */
  surface: number | null;
  /** Absent when the run constrained nothing. */
  distance_to_constraint_mm?: number;
}

/**
 * The strength the material stated over the body's tensile peak (fea/stress.py `margin`): a screening
 * number at a mesh-dependent peak, never a verdict. `factor` is null when the body is nowhere in tension.
 */
export interface FeaStressMargin {
  tensile_strength_mpa: number;
  factor: number | null;
  source: string;
}

/**
 * One peak of fea-service's stress report (fea/stress.py): von Mises recovered per body from CalculiX's displacement
 * field, where it sits, and how far from a constrained node. The principal stresses and the tensile and compressive
 * points arrived on 2026-10-27 (#7730): a run stored before then has none of the three.
 */
export interface FeaStressPeak extends FeaStressPoint {
  von_mises_mpa: number;
  tensor_mpa: { xx: number; yy: number; zz: number; xy: number; yz: number; zx: number };
  /** The tensor's eigenvalues at the von Mises peak node, s1 >= s2 >= s3. */
  principal_mpa?: [number, number, number];
  /** The node of largest s1 over the same nodes: the glass figure (fea/stress.py BASIS cites TIE-33). */
  tensile?: FeaStressPoint & { max_principal_mpa: number };
  /** The node of most negative s3. */
  compressive?: FeaStressPoint & { min_principal_mpa: number };
  /** Bodies only, and only when the material stated a tensile strength. */
  margin?: FeaStressMargin;
}

/**
 * `stress.convergence`, present when the run asked for `mesh.convergence` (fea/stress.py `convergence`): each
 * body's peak von Mises and tensile peak at h and at h / √2 with the signed relative change, null where the
 * coarse value is 0. Only the stress is compared; everything else in the results is the h mesh's.
 */
export interface FeaStressConvergence {
  element_sizes_mm: [number, number];
  bodies: Record<string, {
    von_mises_mpa: [number, number];
    von_mises_change: number | null;
    max_principal_mpa: [number, number];
    max_principal_change: number | null;
  }>;
  basis: string;
}

export interface FeaStressSummary {
  units: 'MPa';
  peak: FeaStressPeak & { body: string };
  /** The body with the largest max principal stress and its tensile point; absent on runs stored before 2026-10-27, as the peaks' principal fields are. */
  tensile_peak?: FeaStressPoint & { body: string; max_principal_mpa: number };
  /** By body name: `lens`, or the assembly's occurrence paths. */
  bodies: Record<string, FeaStressPeak>;
  /** By optical surface number. */
  surfaces: Record<string, FeaStressPeak>;
  basis: string;
  convergence?: FeaStressConvergence;
}

/**
 * A preload with `delta_t_c` (fea/cases.py `_thermal_report`): the three solves on the
 * preload's assembly — the pressure alone, the temperature alone, both — with the seat's
 * axial load in each, the pressed lens's peak von Mises in each, the superposition
 * residual between the three fields and each solve's wall time. `basis` says how the
 * seat was read and why the total cannot change with temperature in this assembly.
 */
export interface FeaPreloadThermal {
  delta_t_c: number;
  seat_load_n: { preload_only: number; soak_only: number; combined: number };
  change_n: number;
  lens_peak_von_mises_mpa: { preload_only: number; soak_only: number; combined: number };
  superposition_residual_mm: number;
  solve_s: { preload_only: number; soak_only: number; combined: number };
  basis: string;
}

/**
 * One seat on the preload's path through the assembly, in path order (fea/assembly.py seat_loads):
 * the lens face on its carrier, the load it should carry and the load read on the carrier's side.
 */
export interface FeaPreloadSeat {
  /** `<lens path>.S<n>`, the face the seat is on. */
  name: string;
  lens: string;
  carrier: string;
  surface: number;
  radius_mm: number;
  nodes: number;
  expected_load_n: number;
  /** Null before the solve. */
  read_load_n: number | null;
  /** |read − expected| / expected; the gate's criterion is 1 %. Null with the load. */
  load_balance_error: number | null;
}

/**
 * fea-service's load-balance report for a preload case (fea/cases.py: a band seat on a
 * parametric lens, or a ring body of a STEP): the seat's total
 * axial reaction against the load it should carry — the ring's force plus whatever
 * of the weight gravity pushes onto the seat (gate G7, spec §0.6). The assembly
 * form adds the ring and the face it presses, the seat circle, the hold's reaction
 * the seat load is derived from, and the thread nodes it unbonds (fea/assembly.py).
 */
export interface FeaPreloadReport {
  /** Assembly form: the pressed ring body and the lens face, by occurrence path. */
  ring?: string;
  face?: string;
  contact_nodes?: number;
  contact_radius_mm?: number;
  seat_face?: string;
  seat_radius_mm?: number;
  /** Assembly form: the pressed ring's nodes duplicated off its thread, so it bears on the glass alone. */
  unbonded_nodes?: number;
  hold_reaction_n?: number | null;
  hold_gravity_n?: number;
  /** Assembly form: the pressed lens's seat load as the hold's total less the bodies between — the cross-check for seats[0]. */
  hold_seat_reaction_n?: number | null;
  hold_balance_error?: number | null;
  /** Assembly form: every seat the preload passes through, the pressed lens's own first (its figures are also seat_reaction_n and load_balance_error). */
  seats?: FeaPreloadSeat[];
  weights_n?: Record<string, number>;
  seat_nodes: number;
  ring_faces: number;
  /** The ring band's area projected along the axis: the face pressure acts normal to the curved face, so pressure × this is the axial force. */
  ring_area_mm2: number;
  pressure_mpa: number;
  weight_n: number;
  expected_seat_load_n: number;
  /** Null when CalculiX printed no total for the seat set. */
  seat_reaction_n: number | null;
  /** |reaction − expected| / expected; the gate's criterion is 1 %. Null with the reaction. */
  load_balance_error: number | null;
  /** Present when the case carried `delta_t_c`; seat_reaction_n and seats are then the combined solve's carrier-side reads, hold_seat_reaction_n the preload-only solve's. */
  thermal?: FeaPreloadThermal;
  basis: string;
}

/** One seat of a seat_model 'contact' run after its last step (fea/cases.py _solve_contact_preload, fea/contact.py seat_read_back). */
export interface FeaContactSeat {
  /** `<lens path>.S<n>`: the pressed ring's seat first, then the composition's seats in order. */
  name: string;
  lens: string;
  carrier: string;
  surface: number;
  radius_mm: number;
  band_area_mm2: number;
  mu: number;
  friction_source: string;
  /** The axial force the glass puts on its carrier, compression positive. */
  normal_force_n: number;
  /** The share of the band's nodes in contact. */
  closed_fraction: number;
  max_slip_mm: number;
  /** The band is closed over less than 5 %. */
  lifted: boolean;
  contact_pressure_mpa: number;
  band_mean_pressure_mpa: number;
  /** What the seat carries once the ring is tightened by statics (the force plus the weight gravity pushes onto it); null off the preload's path. */
  expected_tightened_n: number | null;
  tightened_n: number;
  tighten_balance_error: number | null;
  normal_force_by_step_n: Record<string, number>;
  /** The band's peak contact pressure after each step (the last step's is contact_pressure_mpa). */
  contact_pressure_by_step_mpa: Record<string, number>;
}

export interface FeaContactStep {
  /** tighten, lock, probe (a temperature inside the closed range), soak (the asked change). */
  name: string;
  delta_t_c: number | null;
  /** The pressed seat's normal force after this step. */
  remaining_force_n: number;
  pressed_seat_lifted: boolean;
  ring_advance_mm: number;
  hold_reaction_n: number[];
  in_plane_reaction_n: number[];
  attempts?: number;
  iterations?: number;
}

/**
 * A preload solved with every seat a contact pair (seat_model 'contact', fea/contact.py): the ring tightened by its
 * thread and locked, then the temperature change. `basis` says what the numbers mean and cites the source.
 */
export interface FeaContactPreloadReport {
  seat_model: 'contact';
  ring: string;
  ring_body: string;
  face: string;
  force_n: number;
  delta_t_c: number | null;
  probe_delta_t_c: number | null;
  weights_n: Record<string, number>;
  steps: FeaContactStep[];
  seats: FeaContactSeat[];
  /** The force the locked ring still presses on the glass after the last step. */
  remaining_force_n: number;
  /** The temperature change (signed, °C from the reference) at which the pressed seat opens, read from the solve; null with lift_off_basis saying why. */
  lift_off_c: number | null;
  lift_off_basis: string;
  /** The hand estimate of the loop (fea/contact.py loop_estimate) that sized the probe. */
  estimate: { loop_stiffness_n_per_mm: number; mismatch_mm_per_k: number; preload_change_n_per_k: number; lift_off_delta_t_c: number | null; basis: string };
  solve_s: number;
  nodes: number;
  basis: string;
}

/** The two preload reports: the bonded seat model's load balance, or the contact seat model's remaining preload. */
export type FeaAnyPreloadReport = FeaPreloadReport | FeaContactPreloadReport;

export const isContactPreload = (p: FeaAnyPreloadReport): p is FeaContactPreloadReport => 'seat_model' in p && p.seat_model === 'contact';

/** GET /runs/{id}/results of fea-service, as the run row stores it in `results` (applySigFit's input). */
export interface SigFitFitResults {
  deck: string;
  wavelength_mm: number;
  load_cases: SigFitLoadCaseResult[];
  /** The soak's thermo-optic pass, when the run asked for one. */
  thermo_optic?: SigFitDndtResults;
  /** fea-service's peak-stress report; absent on runs made before it existed. */
  stress?: FeaStressSummary;
  /** A preload case's seat load balance. */
  preload?: FeaAnyPreloadReport;
  /** The stress birefringence OPD map per lens body whose material carried K (spec §4.8); absent otherwise. */
  birefringence?: OptomechBirefringence;
}

export const OptomechRunSchema = z.object({
  id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  status: OptomechRunStatusSchema,
  nominal_system_id: z.string().uuid(),
  nominal_version: z.number().int(),
  /** False once the conversation's current system is no longer the nominal this run was built from. */
  nominal_is_current: z.boolean(),
  /** What went to fea-service: the model, the load case, the wavelength and the mesh. */
  request: OptomechStoredRequestSchema,
  /** fea-service's last state: stage timestamps, mesh size, its error. Null until first polled. */
  fea_state: z.record(z.string(), z.unknown()).nullable(),
  /**
   * SigFit's fitted numbers in lens units, once the run succeeded, with fea-service's
   * reports beside them (`stress`, `preload`, `thermo_optic`, and `birefringence` in the
   * shape of OptomechBirefringenceSchema when a lens material carried K): SigFitFitResults below.
   */
  results: z.record(z.string(), z.unknown()).nullable(),
  cases: z.array(OptomechCaseSummarySchema),
  /** Whether the run's artifacts zip was copied to storage (GET …/artifacts serves it either way while fea-service still holds it). */
  artifacts_stored: z.boolean(),
  /** Per body, the material version expanded or the inline source (spec 2026-10-16 §4.5); null on runs from before it was recorded. */
  materials_used: z.array(OptomechMaterialUsedSchema).nullable(),
  /** Which geometry derivative was meshed (migration 126); null on parametric runs and on rows from before it was recorded. */
  geometry_used: OptomechGeometryUsedSchema.nullable(),
  error: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  finished_at: z.string().nullable(),
});
export type OptomechRun = z.infer<typeof OptomechRunSchema>;

export const OptomechRunListSchema = z.object({ runs: z.array(OptomechRunSchema) });

/** Whether this deployment has a fea-service; where it has none the frontend hides the Optomech tab. */
export const OptomechAvailabilitySchema = z.object({ available: z.boolean() });

export const OptomechCaseSystemSchema = z.object({
  run_id: z.string().uuid(),
  load_case: z.number().int(),
  /** The derived LLM JSON system (validated by the converter's validator when it was written). */
  system: z.record(z.string(), z.unknown()),
  applied: OptomechCaseSummarySchema,
  provenance: z.record(z.string(), z.unknown()),
});
export type OptomechCaseSystem = z.infer<typeof OptomechCaseSystemSchema>;

/**
 * GET /optomech/runs/:id/surface — the solved mesh's outside for the contour
 * viewer (fea-service fea/surface.py): the boundary tet faces as corner
 * triangles wound outward, the midsides dropped, and the nodal fields at those
 * corners. Flat arrays, 0-indexed over the compacted nodes.
 */
export const OptomechSurfaceSchema = z.object({
  // `stress` arrived with `von_mises`; a surface stored before it has neither.
  units: z.object({ length: z.literal('mm'), displacement: z.literal('mm'), temperature: z.literal('C'), stress: z.literal('MPa').optional() }),
  /** x, y, z per node, in the prescription frame. */
  nodes: z.array(z.number()),
  /** The mesh's own node ids, one per node. */
  node_ids: z.array(z.number().int()),
  /** Three node indices per triangle. */
  triangles: z.array(z.number().int()),
  /** Per triangle: the body (physical volume id, keyed in `bodies`) and the optical surface it lies on (0 elsewhere). */
  triangle_body: z.array(z.number().int()),
  triangle_surface: z.array(z.number().int()),
  bodies: z.record(z.string(), z.string()),
  /** ux, uy, uz per node (CalculiX's nodal displacement). */
  displacement: z.array(z.number()),
  /** Per node, the heat solve's temperature for a gradient case; null otherwise. */
  temperature: z.array(z.number()).nullable(),
  /** Per node, the von Mises stress in MPa from each body's element-averaged nodal stress; a node two bodies
   *  share (a bonded interface's rim) carries the larger of the two. Absent on surfaces stored before it. */
  von_mises: z.array(z.number()).nullable().optional(),
});
export type OptomechSurface = z.infer<typeof OptomechSurfaceSchema>;

/**
 * GET /optomech/runs/:id/volume — the solved mesh's inside for the viewer's
 * section cut (fea-service fea/volume.py): every tet at its four corners, the
 * midsides dropped, and the nodal fields at those corners. Flat arrays,
 * 0-indexed over the compacted nodes.
 */
export const OptomechVolumeSchema = z.object({
  units: z.object({ length: z.literal('mm'), displacement: z.literal('mm'), temperature: z.literal('C'), stress: z.literal('MPa') }),
  /** x, y, z per node, in the prescription frame (the surface's frame). */
  nodes: z.array(z.number()),
  /** The mesh's own node ids, one per node: the surface's `node_ids` are a subset. */
  node_ids: z.array(z.number().int()),
  /** Four node indices per tet. */
  tets: z.array(z.number().int()),
  /** Per tet: the body (physical volume id, keyed in `bodies`). */
  tet_body: z.array(z.number().int()),
  bodies: z.record(z.string(), z.string()),
  /** ux, uy, uz per node (CalculiX's nodal displacement). */
  displacement: z.array(z.number()),
  /** Per node, the heat solve's temperature for a gradient case; null otherwise. */
  temperature: z.array(z.number()).nullable(),
  /** Body -> von Mises per node, averaged over that body's tets at the node (a node two bodies share carries each one's own); null at a node of no tet of that body. */
  von_mises: z.record(z.string(), z.array(z.number().nullable())),
});
export type OptomechVolume = z.infer<typeof OptomechVolumeSchema>;

/** One system's wavefront at the compared field and wavelength (the engine's own /wavefront). */
export const OptomechWavefrontSchema = z.object({
  /** RMS wavefront error in waves over the pupil, referenced to the chief ray, tilt not removed. */
  rms_waves: z.number(),
  peak_to_valley_waves: z.number(),
  /** OPD in waves on the pupil, rows increasing toward +Y; null outside the pupil. */
  opd_grid: z.array(z.array(z.number().nullable())),
});

/** GET /optomech/runs/:id/cases/:load_case/compare — the nominal and the derived case through the same engine, and their difference. */
export const OptomechCompareSchema = z.object({
  run_id: z.string().uuid(),
  load_case: z.number().int(),
  engine: z.enum(ANALYSIS_ENGINES),
  nominal_version: z.number().int(),
  field_index: z.number().int(),
  wavelength_index: z.number().int(),
  wavelength_um: z.number(),
  /** The nominal's fields and wavelengths, so a viewer can offer the others. */
  fields: z.array(z.object({ x: z.number(), y: z.number() })),
  wavelengths_um: z.array(z.number()),
  nominal: OptomechWavefrontSchema,
  case: OptomechWavefrontSchema,
  /** Case minus nominal, cell by cell where both are inside the pupil; its RMS and P-V are of that map, not an RMS change. */
  difference: z.object({
    rms_waves: z.number(),
    peak_to_valley_waves: z.number(),
    opd_grid: z.array(z.array(z.number().nullable())),
  }),
  /** What the numbers are: the engine's reference and sampling, stated once for the chart. */
  basis: z.string(),
});
export type OptomechWavefront = z.infer<typeof OptomechWavefrontSchema>;
export type OptomechCompare = z.infer<typeof OptomechCompareSchema>;
